import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type MutableRefObject } from 'react';
import { resolveMediaProfile, type TeleSession } from '@shareview/tele-engine-core';
import { tierProfileOverrides, type PrecallResult } from '@shareview/tele-engine-precall';
import { TeleSessionProvider, useTeleSession, useTeleSnapshot } from '@shareview/tele-engine-react';
import { requestMediaToken } from '@shareview/tele-engine-web';
import { currentAccessToken, ensureSignedIn, signOut, type Principal } from './auth';
import { CallView } from './call-view';
import {
  appConfig,
  displayNameFor,
  parseCallOptions,
  profileFor,
  publishFor,
  type CallMode,
  type CallOptions,
} from './config';
import { Precall } from './precall';
import { createPocSession } from './session';
import { createPocTelemetry, type PocTelemetry } from './telemetry';

type AuthPhase = 'signing-in' | 'signed-in' | 'error';

/** Owns sign-in and the page's single TeleSession. */
export function App({ createSession = createPocSession }: { createSession?: typeof createPocSession }) {
  const [opts, setOpts] = useState<CallOptions>(() => parseCallOptions(window.location.search));
  const [session] = useState<TeleSession>(() => createSession(profileFor(opts)));
  const debugPath = Boolean(opts.directUrl && opts.directToken);
  const [authPhase, setAuthPhase] = useState<AuthPhase>(debugPath ? 'signed-in' : 'signing-in');
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [authError, setAuthError] = useState<string>();
  // Telemetry needs the Calls API token, so the direct-token debug path runs without it.
  const tokenRef = useRef<string | null>(null);
  const [telemetry] = useState<PocTelemetry | null>(() =>
    debugPath ? null : createPocTelemetry(session, () => tokenRef.current),
  );
  useEffect(() => () => void telemetry?.dispose(), [telemetry]);

  useEffect(() => {
    if (debugPath) return undefined;
    let cancelled = false;
    ensureSignedIn()
      .then((p) => {
        if (cancelled || !p) return;
        setPrincipal(p);
        tokenRef.current = p.accessToken;
        // The callback restored the original query string; re-parse it.
        setOpts(parseCallOptions(window.location.search));
        setAuthPhase('signed-in');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setAuthError(`Sign-in failed: ${String(e)}`);
        setAuthPhase('error');
      });
    return () => {
      cancelled = true;
    };
  }, [debugPath]);

  useEffect(() => () => void session.disconnect(), [session]);

  return (
    <TeleSessionProvider session={session}>
      <div className="app">
        <Header opts={opts} principal={principal} debugPath={debugPath} telemetry={telemetry} />
        {authPhase === 'signing-in' && <p className="msg">Signing in via {appConfig.oidcIssuer}...</p>}
        {authPhase === 'error' && <pre className="error msg">{authError}</pre>}
        {authPhase === 'signed-in' && (
          <Call opts={opts} principal={principal} tokenRef={tokenRef} telemetry={telemetry} />
        )}
      </div>
    </TeleSessionProvider>
  );
}

function Header({
  opts,
  principal,
  debugPath,
  telemetry,
}: {
  opts: CallOptions;
  principal: Principal | null;
  debugPath: boolean;
  telemetry: PocTelemetry | null;
}) {
  const state = useTeleSnapshot((s) => s.state);
  const noTelemetry = useCallback(() => () => undefined, []);
  const telemetryStatus = useSyncExternalStore(
    telemetry?.subscribe ?? noTelemetry,
    () => telemetry?.status() ?? 'telemetry off',
  );
  const profile = useTeleSnapshot((s) => s.profile);
  return (
    <header className="bar">
      <strong>Tele POC</strong>
      <span>room: {opts.room}</span>
      <span>profile: {opts.profileBase}</span>
      <span>codec: {profile.codecPolicy}</span>
      <span>
        capture: {profile.captureWidth}x{profile.captureHeight}
      </span>
      <span>simulcast: {profile.simulcast ? 'on' : 'off'}</span>
      {opts.forceRelay && <span>relay-only</span>}
      {debugPath && <span className="warn">DEBUG: direct LiveKit token (SSO/API bypassed)</span>}
      {principal?.source === 'dev-hook' && <span className="warn">DEV: injected access token</span>}
      <span className="spacer" />
      <span data-testid="telemetry">{telemetryStatus}</span>
      <span data-testid="phase">{state}</span>
      {principal?.source === 'sso' && <button onClick={() => void signOut()}>Sign out</button>}
    </header>
  );
}

/** Lobby, join and the in-call view, driven by the session state. */
function Call({
  opts,
  principal,
  tokenRef,
  telemetry,
}: {
  opts: CallOptions;
  principal: Principal | null;
  tokenRef: MutableRefObject<string | null>;
  telemetry: PocTelemetry | null;
}) {
  const session = useTeleSession();
  const state = useTeleSnapshot((s) => s.state);
  const lastError = useTeleSnapshot((s) => s.lastError);
  const disconnectReason = useTeleSnapshot((s) => s.disconnectReason);
  const [mode, setMode] = useState<CallMode>(opts.mode);
  const [joinError, setJoinError] = useState<string>();
  const [fetchingToken, setFetchingToken] = useState(false);
  const autoJoined = useRef(false);
  const [precall, setPrecall] = useState<PrecallResult>();
  const accessToken = useCallback(async () => {
    if (!principal) throw new Error('not signed in');
    const token = await currentAccessToken(principal);
    tokenRef.current = token;
    return token;
  }, [principal, tokenRef]);
  // ?name= is used verbatim (like the TV app's); otherwise mode, user and a per-tab tag.
  const displayName = opts.name ?? displayNameFor(mode, principal?.name ?? 'debug');

  const join = useCallback(async () => {
    setJoinError(undefined);
    try {
      let url: string;
      let token: string;
      if (opts.directUrl && opts.directToken) {
        url = opts.directUrl;
        token = opts.directToken;
      } else {
        if (!principal) throw new Error('not signed in');
        setFetchingToken(true);
        const media = await requestMediaToken({
          apiBaseUrl: appConfig.apiBaseUrl,
          roomId: opts.room,
          accessToken: await accessToken(),
          displayName,
        }).finally(() => setFetchingToken(false));
        url = media.serverUrl;
        token = media.token;
      }
      // The pre-call tier lowers the profile when the network calls for it.
      session.setProfile(
        resolveMediaProfile(profileFor(opts), precall && tierProfileOverrides(precall.recommendedTier)),
      );
      await session.join({ roomId: opts.room, url, token, publish: publishFor(mode), forceRelay: opts.forceRelay });
    } catch (e) {
      console.error('[tele-poc] join failed', e);
      setJoinError(String(e));
    }
  }, [session, opts, principal, mode, displayName, accessToken, precall]);

  const leave = useCallback(() => void session.leave(), [session]);

  // ?autojoin=1 skips the lobby click (automated tests).
  useEffect(() => {
    if (opts.autojoin && !autoJoined.current) {
      autoJoined.current = true;
      void join();
    }
  }, [opts.autojoin, join]);

  if (fetchingToken || state === 'joining' || state === 'connecting') {
    return <p className="msg">Connecting...</p>;
  }
  if (state === 'joined' || state === 'reconnecting' || state === 'leaving') {
    return <CallView onLeave={leave} />;
  }

  const error = joinError ?? (state === 'failed' && lastError ? `${lastError.code}: ${lastError.message}` : undefined);
  return (
    <div className="lobby">
      {error && <pre className="error">{error}</pre>}
      {state === 'disconnected' && disconnectReason && disconnectReason !== 'client' && (
        <p className="warn">Disconnected: {disconnectReason}</p>
      )}
      <fieldset>
        <legend>Mode</legend>
        <label>
          <input type="radio" name="mode" checked={mode === 'nurse'} onChange={() => setMode('nurse')} /> Nurse (camera
          on)
        </label>
        <label>
          <input type="radio" name="mode" checked={mode === 'guest'} onChange={() => setMode('guest')} /> Guest (camera
          off at start)
        </label>
      </fieldset>
      <p>Display name: {opts.directToken ? '(from debug token)' : displayName}</p>
      {mode === 'guest' && principal && (
        <Precall opts={opts} accessToken={accessToken} telemetry={telemetry} onResult={setPrecall} />
      )}
      <button className="primary" data-testid="join" onClick={() => void join()}>
        {state === 'disconnected' ? 'Rejoin' : 'Join'}
      </button>
    </div>
  );
}
